import { useSelector } from "react-redux";
import VLTable from "views/uikit/VLTable";
import { FoldersDropBox } from "../../../../../uikit/FormFields";

import TooltipText from "./TooltipText";
import useCreateDataset from "hooks/useCreateDataset";
import useFolderSelection from "./useFolderSelection";
import useFolderValidation from "hooks/useFolderValidation";
import useFolderData, { FolderTableData } from "./useFolderData";
import { FolderMetadata } from "types";
import { getInvalidFolders } from "redux/CreateDataset/selectors";

import styles from "./style.module.scss";
import Loader from "../../Loader";
import { folderFormats } from "../../../../../uikit/FormFields/FoldersDropBox";
import SupportedFormats from "../../../../../uikit/FormFields/DragAndDropBase/SupportedFormats";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { FoldersPathsForm } from "./FoldersPathsForm";
import { isOnPremUser } from "../../../../../../helpers/utility/utilities";

interface AddDataContentProps {
  isLoading?: boolean;
  folders: FolderMetadata[];
  onFoldersChange: (folders: FolderMetadata[]) => void;
  onFolderUploadStart: (folderNames: string[]) => void;
  isTableLoading?: boolean;
  batchId?: string;
}

export default function AddDataContent({
  folders,
  isLoading = false,
  onFoldersChange,
  onFolderUploadStart,
  isTableLoading,
  batchId,
}: AddDataContentProps) {
  // Get invalid folders from Redux state
  const invalidFolders = useSelector(getInvalidFolders);
  // Use validation hook to get validation state
  const { isValidating } = useFolderValidation(batchId);

  // Combine valid folders with invalid folders for display
  const allFolders = [...folders, ...invalidFolders];
  const hasFolders = allFolders.length > 0;

  const { startFolderUpload, startFoldersPathsHandler, form } =
    useCreateDataset(batchId);
  const {
    selectedRowKeys,
    disabledRowKeys,
    checkboxDisabledKeys,
    checkboxTooltipData,
    filterState,
    filteredFolders,
    handleSelectionChange,
    handleFilterChange,
    showSelectAll,
    handleSelectAll,
  } = useFolderSelection(allFolders, onFoldersChange, batchId);

  const { columns, dataSource } = useFolderData(
    filteredFolders,
    filterState,
    allFolders,
  );

  const handlePaths = ({ paths }: { paths: string[] }) => {
    const normalizedPaths = paths.map((p) => p.trim());
    onFolderUploadStart(normalizedPaths);
    startFoldersPathsHandler(normalizedPaths);
  };

  const handleFilesChange = (newFiles: File[]) => {
    // Extract folder names from the uploaded files
    const folderNames = Array.from(
      new Set(
        newFiles.map((file) => {
          // Get the first directory name from the file path
          const pathParts = file.webkitRelativePath.split("/");
          return pathParts[0]; // First part is the folder name
        }),
      ),
    );

    // Notify parent about the upload start
    onFolderUploadStart(folderNames);

    startFolderUpload(newFiles);
  };

  const showLoadingState = isLoading || isValidating;

  if (isTableLoading) {
    return <div className={styles.tableLoading}>{<Loader />}</div>;
  }

  return (
    <div className={styles.uploadFolderSection}>
      {isOnPremUser() ? (
        <FoldersPathsForm
          form={form}
          onFinish={handlePaths}
          isValidating={isValidating}
        />
      ) : (
        <div className={styles.dropboxWrapper}>
          <FoldersDropBox
            title="Drag and drop folder here"
            textButton="Browse Folders"
            tooltipText={<TooltipText />}
            displayFormats={!hasFolders}
            loading={showLoadingState}
            onFilesChange={handleFilesChange}
          />
        </div>
      )}

      {hasFolders && (
        <div className={styles.fileListSection}>
          <div className={styles.fileListHeaderWrapper}>
            <div className={styles.fileListHeader}>
              Select Folders
              <Tooltip
                placement={"right"}
                title={
                  isOnPremUser() ? (
                    <SelectFoldersTooltipText />
                  ) : (
                    <SupportedFormats formatList={folderFormats} />
                  )
                }
              >
                <InfoCircleOutlined className={styles.infoIcon} />
              </Tooltip>
            </div>
          </div>
          <div className={styles.fileListTable}>
            <VLTable<FolderTableData>
              columns={columns}
              dataSource={dataSource}
              selectedRowKeys={selectedRowKeys}
              disabledRowKeys={disabledRowKeys}
              checkboxDisabledKeys={checkboxDisabledKeys}
              checkboxTooltipData={checkboxTooltipData}
              onTableFilterChange={handleFilterChange}
              onSelectionChange={handleSelectionChange}
              showSelectAll={showSelectAll}
              onSelectAll={handleSelectAll}
              rowKey={"key"}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const SelectFoldersTooltipText = () => (
  <div className={styles.selectFoldersTooltip}>
    <strong>Note</strong>:<br />
    Folder must follow the required structure. In addition, make sure images and
    file contents match, otherwise, the folder will be marked as invalid.
  </div>
);
